import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import { get, put, post } from '@/lib/api-client';
import { env } from '@/lib/env';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useStartImpersonation } from '@/hooks/useImpersonation';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  primaryRegion?: string | null;
  primaryMarket?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface _SearchFilters {
  search?: string;
  role?: string;
  region?: string;
  isActive?: boolean;
}

interface CreateUserForm {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  primaryRegion?: string;
  primaryMarket?: string;
}

const ROLES = [
  { value: 'sales_ae', label: 'Sales AE' },
  { value: 'sales_support', label: 'Sales Support' },
  { value: 'service_ae', label: 'Service AE' },
  // TODO: Temporarily hidden — re-enable when leadership roles are needed
  // { value: 'sales_vp', label: 'Sales VP' },
  // { value: 'gvp', label: 'GVP' },
  // { value: 'rvp', label: 'RVP' },
  { value: 'charting_member', label: 'Charting Member' },
  { value: 'charting_manager', label: 'Charting Manager' },
  { value: 'exceptions_member', label: 'Exceptions Member' },
  // { value: 'adops_leadership', label: 'AdOps Leadership' },
  { value: 'admin', label: 'Admin' },
];

const REGIONS = [
  { value: 'West', label: 'West' },
  { value: 'Central', label: 'Central' },
  { value: 'East', label: 'East' },
];

export default function UserManagement() {
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Users per page
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [bulkImportResult, setBulkImportResult] = useState<{
    success: boolean;
    created: number;
    updated: number;
    skipped: number;
    errors: { row: number; email: string; error: string }[];
  } | null>(null);
  const [newUser, setNewUser] = useState<CreateUserForm>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'sales_ae',
    primaryRegion: '',
    primaryMarket: '',
  });
  const queryClient = useQueryClient();
  const { user: currentUser, isImpersonating } = useAuthContext();
  const startImpersonationMutation = useStartImpersonation();

  // Fetch all users once (no filters in query)
  const { data: usersData, isLoading, error } = useQuery<{ data: { users: User[] } }>({
    queryKey: ['users', 'all'],
    queryFn: async () => {
      return get('/users/search');
    },
  });

  // Filter users locally using useMemo
  const filteredUsers = useMemo(() => {
    const allUsers = usersData?.data?.users || [];
    
    return allUsers.filter((user: User) => {
      // Search filter
      if (searchText) {
        const search = searchText.toLowerCase();
        const matchesSearch = 
          user.email.toLowerCase().includes(search) ||
          user.firstName.toLowerCase().includes(search) ||
          user.lastName.toLowerCase().includes(search);
        
        if (!matchesSearch) return false;
      }

      // Role filter
      if (roleFilter && user.role !== roleFilter) {
        return false;
      }

      // Region filter
      if (regionFilter && user.primaryRegion !== regionFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== '') {
        const isActive = statusFilter === 'true';
        if (user.isActive !== isActive) {
          return false;
        }
      }

      return true;
    });
  }, [usersData, searchText, roleFilter, regionFilter, statusFilter]);

  // Paginate filtered users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (userData: CreateUserForm) => {
      return post('/users', userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        role: 'sales_ae',
        primaryRegion: '',
        primaryMarket: '',
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<User> }) => {
      return put(`/users/${userId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowEditModal(false);
      setEditingUser(null);
    },
    onError: (error) => {
      console.error('Update user error:', error);
    },
  });

  // Toggle user active status
  const toggleUserStatus = async (userId: string, isActive: boolean) => {
    const endpoint = isActive ? 'activate' : 'deactivate';
    
    try {
      await put(`/users/${userId}/${endpoint}`, {});
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (error) {
      console.error(`Error ${endpoint}ing user:`, error);
    }
  };

  // Bulk import mutation
  const bulkImportMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      // Remove default Content-Type to let axios set multipart/form-data with boundary
      return post<{ data?: { success: boolean; created: number; updated: number; skipped: number; errors: { row: number; email: string; error: string }[] } }>('/users/bulk-import', formData, {
        headers: {
          'Content-Type': undefined,
        },
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setBulkImportResult(response.data ?? null);
      setBulkImportFile(null);
    },
  });

  // Download template
  const handleDownloadTemplate = async (format: 'csv' | 'excel' = 'excel') => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_BASE_URL = env.API_BASE_URL;
      
      const response = await fetch(`${API_BASE_URL}/users/bulk-import/template?format=${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Determine file name
      const fileName = format === 'excel' ? 'user-import-template.xlsx' : 'user-import-template.csv';
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBulkImportFile(file);
    setBulkImportResult(null);
  };

  // Process bulk import
  const handleBulkImport = async () => {
    if (!bulkImportFile) return;

    bulkImportMutation.mutate(bulkImportFile);
  };

  const handleEditUser = (user: User) => {
    updateUserMutation.reset();
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleCreateUser = () => {
    if (!newUser.email || !newUser.firstName || !newUser.lastName) {
      return;
    }

    const userData: CreateUserForm = {
      email: newUser.email,
      firstName: newUser.firstName,
      lastName: newUser.lastName,
      role: newUser.role,
    };

    if (newUser.primaryRegion) {
      userData.primaryRegion = newUser.primaryRegion;
    }

    if (newUser.primaryMarket) {
      userData.primaryMarket = newUser.primaryMarket;
    }

    createUserMutation.mutate(userData);
  };

  const handleSaveUser = () => {
    if (!editingUser) return;

    updateUserMutation.mutate({
      userId: editingUser.id,
      updates: {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        role: editingUser.role,
        primaryRegion: editingUser.primaryRegion || null,
        primaryMarket: editingUser.primaryMarket || null,
      },
    });
  };

  const getRoleLabel = (role: string) => {
    return ROLES.find(r => r.value === role)?.label || role;
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? 'success' : 'secondary'}>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message="Failed to load users" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-neutral-900">User Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            Create User
          </Button>
          <Button onClick={() => setShowBulkImportModal(true)}>
            Bulk Import Users
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <Input
            placeholder="Search users..."
            value={searchText}
            onChange={(e) => handleFilterChange(setSearchText, e.target.value)}
          />
          
          <Select
            value={roleFilter}
            onChange={(e) => handleFilterChange(setRoleFilter, e.target.value)}
            options={[
              { value: '', label: 'All Roles' },
              ...ROLES
            ]}
          />

          <Select
            value={regionFilter}
            onChange={(e) => handleFilterChange(setRegionFilter, e.target.value)}
            options={[
              { value: '', label: 'All Regions' },
              ...REGIONS
            ]}
          />

          <Select
            value={statusFilter}
            onChange={(e) => handleFilterChange(setStatusFilter, e.target.value)}
            options={[
              { value: '', label: 'All Status' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]}
          />
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-neutral-600">
          Showing {paginatedUsers.length} of {filteredUsers.length} users
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  Region
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {paginatedUsers.map((user: User) => (
                <tr key={user.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-neutral-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-neutral-400">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant="outline">
                      {getRoleLabel(user.role)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {user.primaryRegion || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-400">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditUser(user)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant={user.isActive ? "outline" : "primary"}
                      size="sm"
                      onClick={() => toggleUserStatus(user.id, !user.isActive)}
                    >
                      {user.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    {currentUser?.role === 'admin' && !isImpersonating && (
                      <span className="relative group inline-block">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!user.isActive || user.role === 'admin' || startImpersonationMutation.isPending}
                          onClick={() => startImpersonationMutation.mutate(user.id)}
                          isLoading={startImpersonationMutation.isPending && startImpersonationMutation.variables === user.id}
                        >
                          Impersonate
                        </Button>
                        {!user.isActive && (
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs rounded bg-neutral-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            Cannot impersonate inactive user
                          </span>
                        )}
                        {user.isActive && user.role === 'admin' && (
                          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs rounded bg-neutral-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            Cannot impersonate another admin
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedUsers.length === 0 && !isLoading && (
          <div className="text-center py-8 text-neutral-600">
            No users found matching your criteria.
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
            <div className="text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create User Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setNewUser({ email: '', firstName: '', lastName: '', role: 'sales_ae', primaryRegion: '', primaryMarket: '' }); }}>
        <div className="bg-white rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Create New User</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({
                  ...newUser,
                  email: e.target.value
                })}
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={newUser.firstName}
                onChange={(e) => setNewUser({
                  ...newUser,
                  firstName: e.target.value
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={newUser.lastName}
                onChange={(e) => setNewUser({
                  ...newUser,
                  lastName: e.target.value
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser({
                  ...newUser,
                  role: e.target.value
                })}
                options={ROLES}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Primary Region
              </label>
              <Select
                value={newUser.primaryRegion || ''}
                onChange={(e) => setNewUser({
                  ...newUser,
                  primaryRegion: e.target.value || undefined
                })}
                options={[
                  { value: '', label: 'No Region' },
                  ...REGIONS
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Primary Market
              </label>
              <Input
                value={newUser.primaryMarket || ''}
                onChange={(e) => setNewUser({
                  ...newUser,
                  primaryMarket: e.target.value || undefined
                })}
                placeholder="Optional"
              />
            </div>

            <div className="bg-outfront-purple-light border border-outfront-purple rounded-lg p-3">
              <p className="text-sm text-outfront-purple">
                User will be created in the system and can sign in via SSO using their email address.
              </p>
            </div>

            {createUserMutation.isError && (
              <ErrorMessage message="Failed to create user. Please check the email is not already in use." />
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewUser({
                  email: '',
                  firstName: '',
                  lastName: '',
                  role: 'sales_ae',
                  primaryRegion: '',
                  primaryMarket: '',
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={createUserMutation.isPending || !newUser.email || !newUser.firstName || !newUser.lastName}
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={showEditModal && !!editingUser} onClose={() => { updateUserMutation.reset(); setShowEditModal(false); setEditingUser(null); }}>
        {editingUser && (
          <div className="bg-white rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  First Name
                </label>
                <Input
                  value={editingUser.firstName}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    firstName: e.target.value
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Last Name
                </label>
                <Input
                  value={editingUser.lastName}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    lastName: e.target.value
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Role
                </label>
                <Select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    role: e.target.value
                  })}
                  options={ROLES}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Primary Region
                </label>
                <Select
                  value={editingUser.primaryRegion || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    primaryRegion: e.target.value || undefined
                  })}
                  options={[
                    { value: '', label: 'No Region' },
                    ...REGIONS
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-1">
                  Primary Market
                </label>
                <Input
                  value={editingUser.primaryMarket || ''}
                  onChange={(e) => setEditingUser({
                    ...editingUser,
                    primaryMarket: e.target.value || undefined
                  })}
                  placeholder="Optional"
                />
              </div>
            </div>

            {updateUserMutation.isError && (
              <ErrorMessage message="Failed to update user. Please try again." />
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveUser}
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={showBulkImportModal} onClose={() => { setShowBulkImportModal(false); setBulkImportFile(null); setBulkImportResult(null); }} size="lg">
          <div className="bg-white rounded-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Bulk Import Users</h2>
            
            <div className="space-y-4">
              <div className="bg-outfront-purple-light border border-outfront-purple rounded-lg p-4">
                <h3 className="font-medium text-outfront-purple mb-2">How it works:</h3>
                <ul className="text-sm text-outfront-purple space-y-1 list-disc list-inside">
                  <li>Upload a CSV file with user information and pre-assigned roles</li>
                  <li>Users are created in the system with their assigned roles</li>
                  <li>When users sign in via SSO, they're automatically matched by email</li>
                  <li>Users immediately have their pre-assigned roles and permissions</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-2">
                  Download Template
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadTemplate('excel')}
                    className="flex-1"
                  >
                    📊 Excel Template (Recommended)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadTemplate('csv')}
                    className="flex-1"
                  >
                    📄 CSV Template
                  </Button>
                </div>
                <p className="text-xs text-neutral-400 mt-1">
                  Excel template includes reference tabs for roles, regions, and markets
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-600 mb-2">
                  Upload File (CSV or Excel)
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-neutral-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-outfront-purple-light file:text-outfront-purple
                    hover:file:bg-outfront-purple-light"
                />
                {bulkImportFile && (
                  <p className="text-sm text-neutral-600 mt-2">
                    Selected: {bulkImportFile.name}
                  </p>
                )}
              </div>

              {bulkImportResult && (
                <div className={`border rounded-lg p-4 ${
                  bulkImportResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <h3 className="font-medium mb-2">Import Results:</h3>
                  <div className="text-sm space-y-1">
                    <p>✅ Created: {bulkImportResult.created}</p>
                    <p>📝 Updated: {bulkImportResult.updated}</p>
                    <p>⏭️ Skipped: {bulkImportResult.skipped}</p>
                    {bulkImportResult.errors.length > 0 && (
                      <div className="mt-3">
                        <p className="font-medium text-red-700">Errors:</p>
                        <ul className="list-disc list-inside text-red-600 max-h-40 overflow-y-auto">
                          {bulkImportResult.errors.map((err: { row: number; email: string; error: string }, idx: number) => (
                            <li key={idx}>
                              Row {err.row} ({err.email}): {err.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {bulkImportMutation.isError && (
                <ErrorMessage message="Failed to import users. Please check your CSV format." />
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkImportModal(false);
                  setBulkImportFile(null);
                  setBulkImportResult(null);
                }}
              >
                Close
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={!bulkImportFile || bulkImportMutation.isPending}
              >
                {bulkImportMutation.isPending ? 'Importing...' : 'Import Users'}
              </Button>
            </div>
          </div>
      </Modal>
    </div>
  );
}